// ─────────────────────────────────────────────
//  Staff domain — the `staff` collection is the SOURCE OF TRUTH for the
//  assignable people pool (doctors, CRNAs, AAs, residents) shown on the board,
//  daily list, roster builder and assignment dropdowns.
//
//  The operational pages historically read the auth `users` collection, whose
//  doctor role string is "anesthesiologist" and whose CRNAs are role
//  "provider" + subRole. This normalizer maps a raw `staff` doc into that SAME
//  board-pool shape so the pages can consume staff without each one re-learning
//  the staff vocabulary.
//
//    staff.role "doctor"                  → { role:"anesthesiologist" }
//    staff.role "crna" | "aa" | "resident"→ { role:"provider", subRole:<that> }
//
//  `uid` links a staff entry to a login account (users/{uid}) when the person
//  signs in — null for pool-only staff who never log in. Alert DELIVERY needs a
//  uid (only a logged-in person can receive a banner), so targeting maps
//  staff → uid downstream.
// ─────────────────────────────────────────────

import { contactEmail, contactPhone } from "./contact";

export const STAFF_DOCTOR_ROLE = "doctor";
export const STAFF_PROVIDER_ROLES = ["crna", "aa", "resident"];

// Board vocabulary (matches what users-sourced pools used).
const BOARD_DOCTOR_ROLE = "anesthesiologist";
const BOARD_PROVIDER_ROLE = "provider";

export function isStaffDoctor(staff) {
  return (staff?.role || "").toLowerCase() === STAFF_DOCTOR_ROLE;
}

// ── THE FORWARD MAPPING: a login profile → the `staff` vocabulary ─────────
//
// `normalizeStaff` below turns staff-speak into profile-speak. This is the same
// translation in the other direction, and it exists because the two collections
// disagreed in production.
//
// ⚠️ THE BUG THIS CLOSES (2026-08-20). An administrator changes somebody from
// CRNA to AA in Setup → Users. That writes `users.subRole = "aa"`. The Team
// directory buckets people by `staff.role` — a DIFFERENT field in a DIFFERENT
// collection — and nothing carried the change across, so the directory went on
// filing them under CRNAs indefinitely. Nine people were mismatched when this
// was found, eight of them the crna→aa edit the owner had just made.
//
// Returns null when the profile does not describe a rosterable person (an
// admin, a scheduler, a board device), so a caller can skip rather than write a
// meaningless role onto a staff record.
export function staffRoleForProfile({ role, subRole } = {}) {
  const r = String(role || "").toLowerCase();
  if (r === "anesthesiologist") return STAFF_DOCTOR_ROLE;
  if (r !== "provider") return null; // admin, scheduler, observer, board devices
  const sub = String(subRole || "").toLowerCase();
  return STAFF_PROVIDER_ROLES.includes(sub) ? sub : null;
}

// Map ONE raw staff doc → the board-pool shape every operational page expects.
// Returns null for an unusable record (no id). Safe on partial docs.
export function normalizeStaff(staff) {
  if (!staff || !staff.id) return null;
  const raw = (staff.role || "").toLowerCase();
  const isDoctor = raw === STAFF_DOCTOR_ROLE;
  return {
    id: staff.id,
    uid: staff.uid || null, // login link (null = pool-only, cannot receive alerts)
    name: staff.name || "",
    role: isDoctor ? BOARD_DOCTOR_ROLE : BOARD_PROVIDER_ROLE,
    subRole: isDoctor ? null : (STAFF_PROVIDER_ROLES.includes(raw) ? raw : "crna"),
    shortCode: staff.shortCode || null,
    goHomeHour: staff.goHomeHour ?? null,
    callOrder: staff.callOrder ?? null,
    // ⚠️ THIS MAPPER IS A WHITELIST. A field it does not name is silently
    // dropped — no error, no failing test, just an empty cell on screen.
    phone: contactPhone(staff),
    // `email` IS BACK, AND IT IS BACK ON PURPOSE (Team directory v2 §1.1).
    //
    // HISTORY, because this field has now been added and removed once:
    // it went in for the directory's staff cross-reference and came out again
    // on 2026-08-19 when that surface narrowed to name + phone. The removal
    // note gave the real reason — "`/staff` is readable by any signed-in
    // account, so every field this normalizer surfaces is a field every signed-
    // in account can read". That objection was about the RULE, not the field.
    //
    // The rule is what changed. `/staff` read is no longer `if isAuth()`: it is
    // a role ALLOWLIST that also requires actual facility membership, so the
    // self-provisioned `observer` and the account nobody has placed at a
    // hospital yet are both refused (firestore.rules, /staff). Re-adding the
    // field without that rule change would repeat the mistake; do not.
    email: contactEmail(staff),
    // Directory opt-out. ABSENT MEANS LISTED — coerced to a strict boolean here
    // so consumers never have to remember the `!== false` rule. See
    // domain/contact.isListed.
    listed: staff.listed !== false,
    employmentType: staff.employmentType || "permanent",
    primaryFacilityId: staff.primaryFacilityId || null,
    facilityIds: staff.facilityIds || null,
    _staff: true, // marks a staff-sourced pool entry
  };
}

// Order a staff pool so the VIEWER's primary facility comes first (asked on
// first login: "Which facility do you work at most often?"), alphabetical
// within each group. Applied to provider dropdowns app-wide via useStaff.
export function sortPoolByPrimaryFacility(pool, primaryFacilityId) {
  const list = Array.isArray(pool) ? pool : [];
  if (!primaryFacilityId) return list;
  return [...list].sort((a, b) => {
    const ap = a?.primaryFacilityId === primaryFacilityId ? 0 : 1;
    const bp = b?.primaryFacilityId === primaryFacilityId ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return (a?.name || "").localeCompare(b?.name || "");
  });
}

// Normalize + split a raw staff list into the doctor / provider pools the board
// consumes. Unusable rows are dropped.
export function splitStaffPool(staffList = []) {
  const all = staffList.map(normalizeStaff).filter(Boolean);
  return {
    all,
    doctors: all.filter((p) => p.role === BOARD_DOCTOR_ROLE),
    providers: all.filter((p) => p.role === BOARD_PROVIDER_ROLE),
  };
}

// Resolve a person id (assignment/roster ids reference staff doc id OR a linked
// uid) to their display entry in a normalized pool. Tolerant of either key so
// existing users-keyed data still resolves during the migration.
export function findInPool(pool = [], id) {
  if (!id) return null;
  return pool.find((p) => p.id === id || p.uid === id) || null;
}

// ── Who may be assigned TODAY ─────────────────
// The staff pool is the whole facility's people; today's ROSTER is who is
// actually working. Assignment pickers must offer the roster, not the pool —
// the mobile relief / next-room sheets were handing out the entire directory,
// so a doctor could stage a relief with someone who is not in the building.
//
// Same rule the board uses (BoardPage's allowedDoctorIds / allowedProviderIds):
// the day's supervisors plus the AIC on the doctor side, the day's CRNAs on the
// provider side, intersected with the pool so each entry keeps its pool fields
// (subRole, shiftCode). A rostered person with no pool entry is dropped, which
// is what the board does too — there is nothing to assign them BY.
export function rosterAssignablePools(roster, doctorPool = [], providerPool = []) {
  const doctorIds = new Set(
    [roster?.aicId, ...((roster?.supervisors || []).map((s) => s?.id))].filter(Boolean)
  );
  const providerIds = new Set(
    ((roster?.crnas || []).map((c) => c?.id)).filter(Boolean)
  );
  return {
    doctors: (doctorPool || []).filter((d) => d && doctorIds.has(d.id)),
    crnas: (providerPool || []).filter((p) => p && providerIds.has(p.id)),
  };
}
